import { Box, Container, Dialog, Paper, useMediaQuery, useTheme } from "@mui/material";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "../../store";
import {
  clearUser,
  createUser,
  deleteUser,
  editUser,
  fetchUserList,
  getRoles,
  getUser,
  resendInvitation,
} from "../../slices/user";
import InfoBox from "../base/InfoBox";
import InfoBoxGroup from "../base/InfoBoxGroup";
import UserTable from "./UserTable";
import Button from "../base/Button";
import {
  PersonAddAlt,
  PeopleAlt,
  SupervisorAccount,
  Add,
} from "@mui/icons-material";
import User from "../../types/user";
import checkRole, { UserRoles } from "../../helpers/checkUserRoles";
import UserStack from "./UserStack";
import Modal from "../base/Modal";
import DeleteConfirmation from "../base/DeleteConfirmation";
import AddEditUser, { UserFormSubmitHandler } from "./AddEditUser";

const Users: FC = (props) => {
  const { id } = useParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { selectedCompany } = useSelector((store) => store.company);
  const { dbUser } = useSelector((store) => store.auth);
  const { status, user, userList } = useSelector((store) => store.user);
  const { ssoStatus } = useSelector((store) => store.provider);

  const formSubmitHandlerRef = useRef<UserFormSubmitHandler | null>(null);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const [modalOpen, setModalOpen] = useState<
    "add" | "edit" | "delete" | "none"
  >("none");
  const [selectedUserId, setSelectedUser] = useState<number | undefined>();
  const [usedId, setUsedId] = useState(false);

  const handleAddNew = () => {
    dispatch(clearUser());
    setModalOpen("add");
  };

  const handleEdit = useCallback(
    (id: number) => {
      dispatch(getUser(id));
      setModalOpen("edit");
    },
    [dispatch]
  );

  const handleInvite = (id: number) => {
    dispatch(resendInvitation(id));
  };

  const handleAppRoute = (id: number, url: string) => {
    window.location.href = `${url}/${id}`;
  };

  const handleOpenDelete = (id: number) => {
    setSelectedUser(id);
    setModalOpen("delete");
  };

  const handleClose = () => {
    setModalOpen("none");
    dispatch(clearUser());
    dispatch(fetchUserList());
    setSelectedUser(undefined);
  };

  const handleSave = async (data: User) => {
    if (modalOpen === "add") {
      await dispatch(createUser(data));
    } else if (modalOpen === "edit") {
      await dispatch(editUser(data));
    }

    handleClose();
  };

  const handleDelete = async () => {
    await dispatch(deleteUser(selectedUserId!));
    handleClose();
  };

  const infoBoxes = [
    {
      text: "Manage user access to SVPro applications by adding and assigning users to your organization.",
      icon: <PersonAddAlt fontSize="inherit" color="secondary" />,
    },
    {
      text: "Control application access by assigning roles, determining what users can see and do.",
      icon: <PeopleAlt fontSize="inherit" color="success" />,
    },
    {
      text: checkRole(dbUser!, UserRoles.GlobalAdmin)
        ? "As a Global Admin, you can view and manage users across all applications in your organization."
        : "As an Application Admin, you can manage users for the applications where you have admin privileges.",
      icon: <SupervisorAccount fontSize="inherit" color="error" />,
    },
  ];

  useEffect(() => {
    if (!selectedCompany) {
      navigate("/select");
    } else if (!selectedCompany?.finishedSetup) {
      navigate("/setup");
    }
  }, [navigate, selectedCompany]);

  useEffect(() => {
    dispatch(fetchUserList());
  }, [dispatch]);

  useEffect(() => {
    if (checkRole(dbUser!, UserRoles.AnyAdmin)) {
      dispatch(getRoles());
    }
  }, [dispatch, dbUser]);

  useEffect(() => {
    if (!!id && !usedId && userList?.length) {
      setUsedId(true);
      handleEdit(+id!);
    }
  }, [id, usedId, userList, handleEdit]);

  return (
    <Container>
      <InfoBoxGroup
        header={`Manage Users for ${selectedCompany?.settings?.companyName}`}
        content={infoBoxes.map((info, index) => {
          return <InfoBox key={index} text={info.text} icon={info.icon} />;
        })}
      />
      <Paper
        sx={{
          paddingY: 3,
          paddingX: { xs: 2, md: 5 },
          marginBottom: 5,
        }}
      >
        {!ssoStatus?.useSso && (
          <Box
            display="flex"
            flexDirection="row"
            justifyContent={{ xs: "center", md: "flex-end" }}
          >
            <Button
              label="Add New User"
              variant="contained"
              onClick={handleAddNew}
              icon={<Add />}
            />
          </Box>
        )}
        <Box display="flex" justifyContent="center" marginY={2} width="100%">
          {isDesktop ? (
            <UserTable
              handleEdit={handleEdit}
              handleDelete={!ssoStatus?.useSso ? handleOpenDelete : undefined}
              handleAppRoute={handleAppRoute}
              handleInvite={handleInvite}
            />
          ) : (
            <UserStack
              handleEdit={handleEdit}
              handleDelete={!ssoStatus?.useSso ? handleOpenDelete : undefined}
              handleInvite={handleInvite}
              handleAppRoute={handleAppRoute}
            />
          )}
        </Box>
      </Paper>
      <Modal
        open={modalOpen === "add" || modalOpen === "edit"}
        onClose={handleClose}
        onSave={() => formSubmitHandlerRef.current?.()}
        title={`${modalOpen === "add" ? "Add New" : "Edit"} User`}
        loading={status === "loading"}
      >
        <AddEditUser
          user={modalOpen === "edit" ? user : undefined}
          type="add"
          onSubmit={handleSave}
          ref={formSubmitHandlerRef}
        />
      </Modal>
      <Dialog open={modalOpen === "delete"}>
        <DeleteConfirmation
          name="this user"
          handleClose={handleClose}
          handleDelete={handleDelete}
          loading={status === "loading"}
        />
      </Dialog>
    </Container>
  );
};

export default Users;
